import React, { memo, useMemo } from 'react';
import { StoryblokAsset } from '../../interfaces/StoryblokAsset.interface';
import { getSbGatsbyImage } from './utils/getSbGatsbyImage.util';
import { GatsbyImage, IGatsbyImageData } from 'gatsby-plugin-image';
import { Sizes } from '../../interfaces/Sizes';
import { imageSizesAsString } from '../../utils/imageSizesAsString/imageSizesAsString.util';
import classNames from 'classnames';

type ObjectFit = 'fill' | 'contain' | 'cover' | 'scale-down' | 'none';

export interface SbImageOptionProps {
  objectFit?: ObjectFit;
  objectPosition?: string;
  width?: number;
  height?: number;
  sizes?: Sizes;
  quality?: number;
  fitIn?: boolean;
  fitInColour?: string;
  smartCrop?: boolean;
  loading?: 'eager' | 'lazy';
  className?: string;
}

interface SbImageProps {
  asset: StoryblokAsset;
  options?: SbImageOptionProps;
}

const isSVG = (image: StoryblokAsset): boolean => {
  if (!image?.filename) return false;
  return ['.svg', '.SVG'].some((extension) =>
    image.filename.includes(extension)
  );
};

const SbImage: React.FC<SbImageProps> = ({ asset, options }) => {
  if (!asset?.filename) return null;

  if (isSVG(asset)) {
    return (
      <img
        src={asset.filename}
        alt={asset.alt}
        title={asset.title}
        style={{
          objectFit: options?.objectFit || 'contain',
          width: options?.width ? `${options?.width}px` : '100%',
          height: options?.height ? `${options?.height}px` : '100%',
        }}
        width={options?.width ? `${options?.width}px` : '100%'}
        height={options?.height ? `${options?.height}px` : '100%'}
        sizes={options?.sizes ? imageSizesAsString(options.sizes) : ''}
        loading={options?.loading || 'lazy'}
        className={options?.className}
      />
    );
  }

  const image: IGatsbyImageData = useMemo(
    () => getSbGatsbyImage(asset, options),
    [asset, options]
  );

  return (
    <GatsbyImage
      image={image}
      alt={asset.alt}
      className={classNames(
        'w-full',
        {
          'h-full': options?.objectFit === 'cover',
        },
        options?.className
      )}
      style={{
        objectFit: options?.objectFit || 'contain',
        maxWidth:
          options?.width && !isNaN(options.width)
            ? `${options.width}px`
            : '100%',
        maxHeight:
          options?.height && !isNaN(options.height)
            ? `${options.height}px`
            : '100%',
      }}
      imgStyle={{
        objectFit: options?.objectFit || 'contain',
        objectPosition: options?.objectPosition || 'center',
      }}
      loading={options?.loading || 'lazy'}
    />
  );
};

export default memo(SbImage);
